/**
 * Policy Routing Engine — In-App Documentation
 *
 * End-to-end guide to PRE for non-technical operators. Single long-scroll
 * page with a sticky table-of-contents sidebar. All content is hard-coded
 * TSX so it ships in one PR and matches the existing portal styling.
 */
import { Link } from '@tanstack/react-router';
import { ArrowLeft, BookOpen } from 'lucide-react';
import {
  DocCallout,
  DocPageLink,
  DocSection,
  DocStepList,
  DocTOC,
  MermaidDiagram,
  DIAGRAM_HOW_IT_WORKS,
  DIAGRAM_THREE_LAYERS,
  DIAGRAM_JURISDICTIONS,
  DIAGRAM_RULE_STRUCTURE,
  DIAGRAM_CALLING_HOURS,
  DIAGRAM_CONTACT_CREATION,
  DIAGRAM_DECISION_STATES,
  DIAGRAM_AUTHORING_LIFECYCLE,
  DIAGRAM_LAYER_DECISION,
} from '../../components/docs';

// ============================================
// Single source of truth for sections.
// Used by both the TOC and the content below.
// ============================================
const SECTIONS = [
  { id: 'welcome', title: 'Welcome' },
  { id: 'how-it-works', title: 'How PRE Works' },
  { id: 'three-layers', title: 'The Three Layers' },
  { id: 'jurisdictions', title: 'Jurisdictions' },
  { id: 'rules', title: 'Rules' },
  { id: 'authoring-guide', title: 'Authoring a Rule' },
  { id: 'checks', title: 'The Checks Library' },
  { id: 'calling-hours', title: 'Calling Hours' },
  { id: 'dnc', title: 'DNC Management' },
  { id: 'frequency-caps', title: 'Frequency Caps' },
  { id: 'contact-creation', title: 'Contact Creation' },
  { id: 'ai-classification', title: 'AI Classification' },
  { id: 'the-decision', title: 'Reading the Decision' },
  { id: 'decision-log', title: 'The Decision Log' },
  { id: 'scenarios', title: 'Common Scenarios' },
  { id: 'bundles-backups', title: 'Bundles & Backups' },
  { id: 'glossary', title: 'Glossary' },
];

// ============================================
// Authoring guide — check catalog data + card
// ============================================
//
// One row per check id the engine recognises. Statuses reflect the actual
// switch statement in services/api-gateway/src/services/pre-engine.service.ts
// and the mirror at services/workers/src/lib/pre-engine.ts. Update both this
// file and the engine when adding a new check.
interface CheckEntry {
  id: string;
  purpose: string;
  usage: string;
  params?: string;
  inputs?: string;
}

const CHECK_CATALOG: {
  live: CheckEntry[];
  tenantConfig: CheckEntry[];
  pending: CheckEntry[];
} = {
  live: [
    {
      id: 'CHK_CONTACT_ELIGIBLE',
      purpose: 'Verifies the contact has eligibility (green flag) for the requested channel × purpose.',
      usage: 'Use as the baseline check on most Eligibility (Layer 1) rules.',
    },
    {
      id: 'CHK_CONSENT_AT_LEAST',
      purpose: "Strictly verifies the recipient's stored consent meets a minimum level (eg. EXPRESS_OPTIN, WRITTEN_EXPRESS_OPTIN).",
      params: 'minConsent (default EXPRESS_OPTIN)',
      usage: 'Use when a statute requires a specific level above the eligibility baseline (eg. prerecorded marketing voice requires WRITTEN_EXPRESS_OPTIN).',
    },
    {
      id: 'CHK_INTERNAL_DNC_CLEAR',
      purpose: 'Verifies the recipient is not on the tenant\'s internal Do-Not-Contact list.',
      usage: 'Use on any rule where a tenant-curated suppression must override consent.',
    },
    {
      id: 'CHK_NATIONAL_REGISTRY_CLEAR',
      purpose: 'Verifies the recipient is not on a named national DNC registry (US, BR, MX, etc.).',
      params: 'registry (optional — falls back to the aggregate status)',
      usage: 'Use for jurisdiction-specific DNC enforcement (eg. US-DNC, BR Não Me Perturbe).',
    },
    {
      id: 'CHK_NATIONAL_REGISTRY_SCRUBBED',
      purpose: 'Verifies the registry status is clear AND the scrub timestamp is within a freshness window.',
      params: 'registry, maxAgeDays (typically 31)',
      usage: 'Use when the statute requires periodic re-scrubbing of the list (eg. US TSR 31-day rule).',
    },
    {
      id: 'CHK_WITHIN_CALLING_HOURS',
      purpose: "Verifies the current time at the recipient is inside a permitted sending window for the jurisdiction.",
      usage: 'Use on Layer 2 constraint rules that defer or block outside-of-hours sends. Pair with a CONDITION effect to defer rather than reject.',
    },
    {
      id: 'CHK_FREQUENCY_CAP_24H_OK',
      purpose: 'Verifies attempts to the same recipient on the same subject in the last 24 hours are under a cap.',
      params: 'cap (default 3)',
      usage: 'Use on Layer 2 constraint rules to prevent over-contact.',
    },
    {
      id: 'CHK_FREQUENCY_CAP_7D_OK',
      purpose: 'Verifies cross-channel contacts to the same recipient on the same subject in the last 7 days are under a cap.',
      params: 'cap (default 1)',
      usage: 'Use for stricter weekly limits that span SMS + Email + Voice on a single campaign subject.',
    },
    {
      id: 'CHK_CHANNEL_PREFERENCE_MATCH',
      purpose: "Verifies, if the recipient has a preferred channel set, that the requested channel matches.",
      usage: "Use on Layer 2 rules that honor recipient channel preferences. Skips evaluation when no preference is set.",
    },
  ],
  tenantConfig: [
    {
      id: 'CHK_WIRELESS_MARKETING_CONSENT_OK',
      purpose: 'TCPA wireless gate. Mobile numbers require the rule\'s declared consent level; landline/VOIP fall back to the standard channel gate.',
      params: 'ifWirelessMinConsent (default EXPRESS_OPTIN)',
      inputs: 'Contact.lineType (MOBILE / LANDLINE / VOIP / UNKNOWN); falls back to eligibility-flag proxy at the baseline level.',
      usage: 'Use on any TCPA-bound marketing-SMS rule. Pair with CHK_CONSENT_AT_LEAST when you want strict per-cell enforcement.',
    },
    {
      id: 'CHK_REVOCATION_HANDLING_ENABLED',
      purpose: 'Verifies the tenant has attested that the STOP keyword (SMS) or the unsubscribe endpoint (email) is wired up and live.',
      inputs: 'Tenant.smsStopHandlerEnabled / Tenant.emailUnsubHandlerEnabled. Fails closed if absent.',
      usage: 'Use as a precondition on any marketing rule. Onboard the tenant once; rule stops blocking from then on.',
    },
    {
      id: 'CHK_SENDER_ID_AND_UNSUB_PRESENT',
      purpose: 'CAN-SPAM. Verifies the rendered marketing email contains a working unsubscribe link, a physical postal address, and an identifiable From header.',
      params: 'postalAddressRequired (default true), headerAccuracyRequired (default true)',
      inputs: 'Rendered email template signals + Tenant.postalAddress. Fails closed if either is missing.',
      usage: 'Use on every marketing-email rule. The delivery worker populates the signals after the template renders.',
    },
    {
      id: 'CHK_CASL_CONSENT_VALID',
      purpose: 'CASL (Canada). Verifies the recipient has valid express or implied consent within the regulated windows.',
      params: 'impliedPurchaseWindowMonths (default 24), impliedInquiryWindowMonths (default 6)',
      inputs: "Currently proxies to the contact's eligibility flag. Tighten the upstream consent capture to make this check meaningful.",
      usage: 'Use on Canadian marketing rules across SMS and Email.',
    },
    {
      id: 'CHK_CASL_CONSPICUOUS_PUBLICATION_VALID',
      purpose: 'CASL B2B exemption. Verifies the contact\'s address was conspicuously published and the other §10(9) conditions are met.',
      params: 'requiresEvidenceSnapshot, requiresRelevance, requiresNoDisclaimer (all default true)',
      inputs: 'Currently proxies to the eligibility flag.',
      usage: 'Use on CA B2B marketing-email rules where you rely on the conspicuous-publication exemption rather than consent.',
    },
    {
      id: 'CHK_LGPD_LAWFUL_BASIS_VALID',
      purpose: 'LGPD (Brazil). Verifies the send has a valid lawful basis (consent or legitimate interest with a Legitimate Interest Assessment).',
      params: 'preferred (consent | legitimate_interest), allowLegitimateInterestWithLIA (default true)',
      inputs: 'Currently proxies to the eligibility flag.',
      usage: 'Use on Brazilian marketing rules. Pair with CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR when that check becomes live.',
    },
  ],
  pending: [
    {
      id: 'CHK_BRAZIL_0303_PREFIX',
      purpose: 'Verifies outbound voice caller-ID uses the Brazilian 0303 prefix for telemarketing.',
      usage: 'Reserved for the voice-channel work. Currently a no-op; do not gate sends on it.',
    },
    {
      id: 'CHK_BRAZIL_NAO_ME_PERTURBE_CLEAR',
      purpose: 'Verifies the recipient is not on the Brazilian Não Me Perturbe registry.',
      usage: 'Awaiting registry integration. Once live, pair with CHK_LGPD_LAWFUL_BASIS_VALID on BR marketing rules.',
    },
    {
      id: 'CHK_AUTOMATION_ALLOWED_FOR_SEND',
      purpose: 'Verifies the dialing or selection automation mode is allowed for the send (per jurisdiction).',
      usage: 'Reserved for voice automation enforcement. Currently a no-op.',
    },
    {
      id: 'CHK_END_CALL_WITHIN_10S_ON_REQUEST',
      purpose: 'Verifies the voice script ends the call within 10 seconds when the recipient requests termination (eg. Washington).',
      usage: 'Reserved for voice scripts; out of scope for SMS/Email rules.',
    },
    {
      id: 'CHK_DNC_DISCLOSURE_AT_CALL_START',
      purpose: 'Verifies the voice script reads the DNC disclosure at the start of the call (eg. New York).',
      usage: 'Reserved for voice scripts.',
    },
    {
      id: 'CHK_RIGHT_TO_DISCONNECT_OK',
      purpose: 'Verifies the send respects the recipient\'s right-to-disconnect / business-hours protection (eg. Brazil, France-style rules).',
      usage: 'Reserved. Pair with CHK_WITHIN_CALLING_HOURS for business-hours enforcement until this check goes live.',
    },
  ],
};

function CheckCard({ id, purpose, usage, params, inputs }: CheckEntry) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/50 dark:bg-slate-900/30">
      <div className="font-mono text-xs text-slate-700 dark:text-slate-300 break-all">{id}</div>
      <div className="mt-1.5 text-sm text-slate-700 dark:text-slate-300">{purpose}</div>
      {params && (
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          <strong>Params:</strong> {params}
        </div>
      )}
      {inputs && (
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          <strong>Needs:</strong> {inputs}
        </div>
      )}
      <div className="mt-1.5 text-xs italic text-slate-500 dark:text-slate-400">
        {usage}
      </div>
    </div>
  );
}

// ============================================
// Page
// ============================================

export function PolicyDocs() {
  return (
    <div className="flex flex-col h-full">
      {/* Page Header — matches PolicyEngine pattern */}
      <div className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-4">
          <Link
            to="/settings/policy"
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-100 to-violet-200 dark:from-violet-900/40 dark:to-violet-800/40 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-violet-600 dark:text-violet-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              PRE Documentation
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              End-to-end guide to the Policy Routing Engine — setup, management, and operation.
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-8 flex gap-10">
          <DocTOC entries={SECTIONS} />

          <main className="flex-1 min-w-0 max-w-3xl">
            {/* =================================================== */}
            {/* 1. Welcome                                          */}
            {/* =================================================== */}
            <DocSection
              id="welcome"
              eyebrow="Section 1"
              title="Welcome — What is the Policy Routing Engine?"
            >
              <p>
                The <strong>Policy Routing Engine</strong> — or <strong>PRE</strong> — is the system that
                decides whether any outbound message may legally be sent, and logs every decision for
                audit. Every SMS, email, or voice call that Respectly is asked to send is run through PRE
                first.
              </p>
              <p>
                PRE answers one question, in plain terms: <em>&ldquo;May we send this message, to this
                recipient, on this channel, right now?&rdquo;</em> The answer is one of three outcomes —{' '}
                <strong>Allow</strong>, <strong>Allow with Conditions</strong>, or <strong>Block</strong>{' '}
                — and the reasoning behind the answer is recorded in the Decision Log so you can defend
                it later.
              </p>
              <p>
                This guide walks you through everything you need to set PRE up, operate it day to day,
                and read the decisions it produces. It is written for operators, compliance leads, and
                support staff. No engineering background required.
              </p>
              <DocCallout variant="tip" title="How to use this guide">
                Use the table of contents on the left to jump between sections. Each section that
                references a live admin page includes a pill-style link that takes you straight to it,
                so you can read the guide and click into the matching page side by side.
              </DocCallout>
            </DocSection>

            {/* =================================================== */}
            {/* 2. How PRE Works                                    */}
            {/* =================================================== */}
            <DocSection
              id="how-it-works"
              eyebrow="Section 2"
              title="How PRE Works in 60 Seconds"
            >
              <p>
                When the app is asked to send a message, PRE does five things, in order:
              </p>
              <DocStepList
                steps={[
                  {
                    title: 'Resolve the jurisdiction',
                    description:
                      'PRE figures out what laws apply by looking at the recipient — usually the country code or area code of the phone number, or the state on the contact record.',
                  },
                  {
                    title: 'Find applicable rules',
                    description:
                      'PRE loads every active rule that matches this jurisdiction, the channel (SMS, Email, Voice), the purpose (Marketing or Informational), and whether the recipient is a business or a consumer.',
                  },
                  {
                    title: 'Run eligibility checks',
                    description:
                      'These are the consent and Do-Not-Contact checks. If the recipient has not opted in (where opt-in is required), or appears on a Do-Not-Contact list, the send is blocked here.',
                  },
                  {
                    title: 'Run constraint checks',
                    description:
                      'These are the operational checks — calling hours, frequency caps, content compliance. They can block the send, or attach conditions (for example, "send only between 9 AM and 9 PM in the recipient\'s local time").',
                  },
                  {
                    title: 'Produce a decision and log it',
                    description:
                      'The outcome — Allow, Allow with Conditions, or Block — is returned to whoever asked, and the full reasoning is written to the Decision Log.',
                  },
                ]}
              />
              <MermaidDiagram
                chart={DIAGRAM_HOW_IT_WORKS}
                caption="The end-to-end PRE flow. Every send request follows this path."
              />
              <p>
                That is the whole engine. Everything else in this guide is detail — what each check
                category does, how to author the rules, how to read the audit trail, and where each
                setting lives in this portal.
              </p>
            </DocSection>

            {/* =================================================== */}
            {/* 3. The Three Layers                                 */}
            {/* =================================================== */}
            <DocSection
              id="three-layers"
              eyebrow="Section 3"
              title="The Three Layers"
            >
              <p>
                PRE is organised into three layers. Layers exist because compliance happens at different
                moments in a contact&apos;s lifecycle, and grouping the rules by moment keeps the system
                understandable.
              </p>
              <MermaidDiagram
                chart={DIAGRAM_THREE_LAYERS}
                caption="The three layers, and when each one fires."
              />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Layer 0 — Consent Capture
              </h3>
              <p>
                <strong>Fires once, the moment a contact is created.</strong> Layer 0 decides which
                channels and purposes get an automatic consent record on contact creation. In most US
                states, informational SMS is auto-granted (your customer expects you to be able to send
                a delivery confirmation). Marketing is almost never auto-granted — it requires an
                explicit opt-in.{' '}
                <DocPageLink to="/settings/policy/consent-capture" label="Open Contact Creation rules" />
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Layer 1 — Eligibility
              </h3>
              <p>
                <strong>Fires before every send.</strong> Layer 1 answers, &ldquo;is this recipient
                eligible to be contacted at all on this channel and purpose?&rdquo; The checks here are
                the absolutes: do they have consent? Are they on an internal Do-Not-Contact list? Are
                they on a national registry? If any block-effect check fails in Layer 1, the send is
                stopped immediately and Layer 2 does not even run.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Layer 2 — Constraints
              </h3>
              <p>
                <strong>Fires after Layer 1 passes, at send time.</strong> Layer 2 is the operational
                layer — the &ldquo;you may send, but only under these conditions&rdquo; layer. This is
                where calling hours, frequency caps, and content compliance live. Layer 2 typically
                emits conditions (defer the send to a permitted window, throttle to one message a day,
                require an unsubscribe link) rather than outright blocks.
              </p>
              <DocCallout variant="info" title="Why split eligibility and constraints?">
                Eligibility is about permission. Constraints are about good manners. A contact who has
                opted in is eligible — but you still cannot text them at 3 AM, and you still cannot
                hammer them with five messages a day. Separating the two keeps the rules legible.
              </DocCallout>
            </DocSection>

            {/* =================================================== */}
            {/* 4. Jurisdictions                                    */}
            {/* =================================================== */}
            <DocSection
              id="jurisdictions"
              eyebrow="Section 4"
              title="Jurisdictions"
            >
              <p>
                A <strong>jurisdiction</strong> is a place whose laws govern what you may send. PRE
                ships with the United States (federal + every state and DC), Canada, Brazil, Mexico,
                Colombia, Argentina, Chile, Peru, and a top-level <em>GLOBAL</em> baseline that applies
                everywhere.
              </p>
              <MermaidDiagram
                chart={DIAGRAM_JURISDICTIONS}
                caption="Jurisdictions form a tree. A rule defined higher up applies to everything below it."
              />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                How a contact gets a jurisdiction
              </h3>
              <p>
                For phone-based channels, PRE reads the country code, and — for North American numbers
                — the three-digit area code, and maps it to the right state or country. For email, PRE
                uses the contact&apos;s state field if it is set; otherwise it falls back to the phone
                number.
              </p>
              <DocCallout variant="warning" title="Heads up: area-code guessing">
                When PRE has to guess a US state from the area code, it attaches a small warning to the
                decision called a <em>risk flag</em>. Area codes can travel with the person who ported
                the number, so this guess is occasionally wrong. If you want certainty, set the
                contact&apos;s state field explicitly.
              </DocCallout>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Inheritance
              </h3>
              <p>
                Rules defined higher in the tree apply to everything below them. A rule on GLOBAL fires
                everywhere. A rule on US-FED fires for every US contact. A rule on US-CA fires only
                for California. When you author a rule, you pick the most specific jurisdiction it
                belongs on — and PRE handles the inheritance for you.
              </p>
              <DocPageLink to="/settings/policy/jurisdictions" label="Open Jurisdiction Rules" />
            </DocSection>

            {/* =================================================== */}
            {/* 5. Rules                                            */}
            {/* =================================================== */}
            <DocSection
              id="rules"
              eyebrow="Section 5"
              title="Rules — What They Are and How to Author One"
            >
              <p>
                A <strong>rule</strong> is a single compliance requirement, expressed as a set of
                attributes plus a list of checks to run. PRE evaluates each applicable rule for every
                send, and combines the results to produce the final decision.
              </p>
              <MermaidDiagram
                chart={DIAGRAM_RULE_STRUCTURE}
                caption="Every rule has six attributes and produces one of three effects."
              />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                The six attributes of a rule
              </h3>
              <ul className="space-y-2 list-disc pl-5">
                <li>
                  <strong>Layer</strong> — Consent Capture, Eligibility, or Constraint. Picks when the
                  rule fires.
                </li>
                <li>
                  <strong>Channel</strong> — SMS, Email, Voice, or any channel. Limits the rule to
                  specific transports.
                </li>
                <li>
                  <strong>Purpose</strong> — Marketing, Informational, or any purpose. Most rules are
                  purpose-specific.
                </li>
                <li>
                  <strong>B2B / B2C</strong> — Whether the rule targets business or consumer
                  recipients (or both).
                </li>
                <li>
                  <strong>Required Checks</strong> — The list of compliance checks that must pass for
                  the rule to be satisfied.
                </li>
                <li>
                  <strong>Effect</strong> — What happens if the checks fail: <strong>Block</strong>{' '}
                  stops the send; <strong>Condition</strong> attaches a requirement (eg. wait for
                  calling hours); <strong>Allow</strong> is rare and explicit.
                </li>
              </ul>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Walkthrough — &ldquo;Require explicit consent for marketing SMS in California&rdquo;
              </h3>
              <DocStepList
                steps={[
                  {
                    title: 'Open Jurisdiction Rules',
                    description: 'Navigate to the Jurisdiction Rules page in the Policy section.',
                  },
                  {
                    title: 'Pick the US-CA jurisdiction',
                    description:
                      'Use the jurisdiction selector at the top of the page to switch to California.',
                  },
                  {
                    title: 'Find the SMS — Marketing tile and click Add Rule',
                    description:
                      'The page shows a grid of channel × purpose tiles. Each tile lists the rules that fire for that combination.',
                  },
                  {
                    title: 'Configure the new rule',
                    description:
                      'Set layer to Eligibility, channel to SMS, purpose to Marketing, B2B/B2C to B2C, effect to Block. Add the consent check and set its minimum consent level to written express opt-in.',
                  },
                  {
                    title: 'Fill in the audit fields',
                    description:
                      'Add a citation (statute name or section number), a tenant-facing summary, and a remediation note ("capture written express consent before retrying"). These show up in the Decision Log later.',
                  },
                  {
                    title: 'Save',
                    description:
                      'The rule is now active. Every marketing SMS to a California consumer will be evaluated against it on the next send.',
                  },
                ]}
              />
              <DocPageLink to="/settings/policy/jurisdictions" label="Open Jurisdiction Rules" />
            </DocSection>

            {/* =================================================== */}
            {/* 6. Authoring a Rule                                 */}
            {/* =================================================== */}
            <DocSection
              id="authoring-guide"
              eyebrow="Section 6"
              title="Authoring a Rule — Field-by-Field Reference"
            >
              <p>
                This is the deep-dive companion to Section 5. If Section 5 is
                &ldquo;here is what a rule is,&rdquo; this section is &ldquo;here is exactly what to type
                in every field, what each one does behind the scenes, and what goes wrong if you get
                it wrong.&rdquo; Read it once end-to-end; refer back to the field reference whenever
                you author or edit a rule.
              </p>

              <DocCallout variant="danger" title="Save = Live. There is no draft mode.">
                The moment you click <strong>Save Changes</strong>, the rule is written to the
                production rules table and the delivery worker will consult it on the very next
                outbound message. There is no staging step, no &ldquo;publish later,&rdquo; no
                preview-only mode. Previous versions of the rule are kept in the audit log so you
                can see what changed and when, but a bad rule will affect real traffic immediately.
                If you are uncertain, prefer to deactivate the rule (Section 6.7) and re-enable it
                after review.
              </DocCallout>

              <DocCallout variant="tip" title="When to author a new rule vs. edit an existing one">
                <strong>New rule</strong> when a new jurisdiction comes online, a new statute is
                enacted, or you need a different effect/channel/purpose combination. <strong>Edit
                the existing rule</strong> when fixing typos, clarifying the tenant-facing summary,
                tightening a parameter (eg. lower a frequency cap), or updating a citation. Every
                edit creates a versioned snapshot, so you never lose the prior wording.
              </DocCallout>

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.1 The rule lifecycle
              </h3>
              <p>
                What actually happens when you click Save, and how the rule ends up affecting a
                real message:
              </p>
              <MermaidDiagram
                chart={DIAGRAM_AUTHORING_LIFECYCLE}
                caption="From Save in the form to the Decision Log entry for a real send. The dotted edge is the time gap between save and the next inbound send — usually milliseconds, but conceptually a separate event."
              />

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.2 Field-by-field reference
              </h3>
              <p>
                Every field in the Add / Edit Rule modal, in the order it appears. Each field has
                four lines: <strong>what it is</strong>, <strong>what it does at runtime</strong>,{' '}
                <strong>what to type</strong>, and <strong>if you get it wrong</strong>.
              </p>

              <div className="space-y-5 mt-4">
                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Rule ID</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The unique identifier for this rule.</li>
                    <li><strong>What it does at runtime:</strong> Used by the Decision Log and the rule trace to refer to this rule. Stable across edits.</li>
                    <li><strong>What to type:</strong> Auto-generated, leave alone. New custom rules get an ID like <code>CUSTOM-US-CA-001</code>.</li>
                    <li><strong>If you get it wrong:</strong> You cannot; the field is read-only.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Name</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The human-readable label for the rule, shown to operators in lists and in the Decision Log trace.</li>
                    <li><strong>What it does at runtime:</strong> Display only. Does not affect evaluation.</li>
                    <li><strong>What to type:</strong> A short, scannable summary, eg. <em>&ldquo;US-CA &mdash; BLOCK Marketing SMS without written consent.&rdquo;</em> For Layer 0 (Consent Capture) rules, this field is hidden and the name is auto-generated.</li>
                    <li><strong>If you get it wrong:</strong> Other operators will be confused when reading the Decision Log; no functional impact.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Description</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> Internal free-text note about the rule&apos;s intent.</li>
                    <li><strong>What it does at runtime:</strong> Nothing &mdash; internal context only. Never shown to tenants.</li>
                    <li><strong>What to type:</strong> The reason the rule exists, the statute it implements, or any operational caveat. Eg. <em>&ldquo;Implements TCPA written-consent requirement for prerecorded marketing calls; written for the May 2026 FCC update.&rdquo;</em></li>
                    <li><strong>If you get it wrong:</strong> Future operators lose the &ldquo;why.&rdquo; No runtime impact.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Layer</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> When in a contact&apos;s lifecycle the rule fires &mdash; at contact creation, before send, or at send-time.</li>
                    <li><strong>What it does at runtime:</strong> Determines which evaluation pass picks up the rule. See Section 6.4 for the decision aid.</li>
                    <li><strong>What to type:</strong> <strong>Consent Capture (Layer 0)</strong> for rules that grant initial consent at contact creation. <strong>Eligibility (Layer 1)</strong> for &ldquo;can we contact this person at all?&rdquo; gates. <strong>Constraint (Layer 2)</strong> for &ldquo;under what conditions?&rdquo; gates.</li>
                    <li><strong>If you get it wrong:</strong> The rule may never fire (eg. Layer 2 will never run if Layer 1 already blocked), or may fire at the wrong moment (eg. a calling-hours rule placed in Layer 0 has no effect).</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Effect</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> What the engine does when this rule fires.</li>
                    <li><strong>What it does at runtime:</strong> <strong>BLOCK</strong> short-circuits evaluation &mdash; the message is suppressed and Layer 2 is skipped if the BLOCK fires in Layer 1. <strong>CONDITION</strong> attaches a follow-up requirement (eg. &ldquo;defer to calling hours&rdquo;); the message can still send, but with the condition applied. <strong>ALLOW</strong> is a fall-through; it does <em>not</em> override other BLOCK rules. Layer 0 is the only place ALLOW grants a permission (initial consent record).</li>
                    <li><strong>What to type:</strong> Pick BLOCK for safety-critical gates (no consent → no send). Pick CONDITION for operational constraints (frequency caps, calling hours). Reserve ALLOW for Layer 0 consent-capture rules.</li>
                    <li><strong>If you get it wrong:</strong> A BLOCK where CONDITION was intended will hard-stop sends that should have been deferred. A CONDITION where BLOCK was intended will let non-compliant sends through with a meaningless annotation.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Channel</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The transport this rule applies to.</li>
                    <li><strong>What it does at runtime:</strong> The engine skips this rule for any send whose channel does not match. Blank = matches every channel.</li>
                    <li><strong>What to type:</strong> SMS, EMAIL, or VOICE for channel-specific statutes (TCPA wireless is SMS-only; CAN-SPAM is EMAIL-only). Leave blank when the rule should apply across all transports (eg. a global DNC list).</li>
                    <li><strong>If you get it wrong:</strong> Setting EMAIL on a TCPA rule will silently exempt SMS from the rule. Always double-check that the channel matches the underlying statute.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Purpose</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> Whether the rule applies to Marketing or Informational messages.</li>
                    <li><strong>What it does at runtime:</strong> The engine reads the AI-classified purpose of the campaign and skips this rule for non-matching purposes. Blank = matches both.</li>
                    <li><strong>What to type:</strong> MARKETING for promotional offers, calls-to-action, and ad copy. INFORMATIONAL for delivery confirmations, appointment reminders, password resets, account alerts. Blank for purpose-agnostic rules (eg. internal DNC).</li>
                    <li><strong>If you get it wrong:</strong> A consent rule scoped MARKETING will not stop a campaign that was mistakenly tagged INFORMATIONAL. Check the AI Classification queue when in doubt.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">B2B / B2C</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> Whether the rule applies only to business contacts, only to consumer contacts, or to both.</li>
                    <li><strong>What it does at runtime:</strong> Filters the rule against <code>Contact.b2bOrB2c</code>. Important quirk: when set on the rule, contacts with a <em>null</em> B2B/B2C field will <strong>not match</strong>. Blank = matches both, including nulls.</li>
                    <li><strong>What to type:</strong> B2C for consumer-protection statutes (TCPA wireless, US national DNC, CASL consumer rules). B2B for B2B-exempt rules. Leave blank when you are not sure your contact data has the field populated reliably.</li>
                    <li><strong>If you get it wrong:</strong> Scoping a national DNC rule as B2C while half your contacts have a null B2B/B2C value means those contacts will skip the DNC check entirely.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Priority</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The order rules within the same layer are evaluated in. Lower number = evaluated first.</li>
                    <li><strong>What it does at runtime:</strong> The engine sorts matching rules by priority ascending. The first BLOCK-effect rule that fails short-circuits the layer.</li>
                    <li><strong>What to type:</strong> <strong>0&ndash;50</strong> for safety-critical BLOCK rules that must evaluate first (eg. internal DNC, prerecorded-voice consent). <strong>100</strong> as a sensible default. <strong>200+</strong> for non-critical conditions.</li>
                    <li><strong>If you get it wrong:</strong> A safety-critical rule at priority 200 may never get a chance to BLOCK because a less-critical rule at priority 100 already short-circuited the layer.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Required Checks</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The list of yes/no questions the engine must answer for this rule. Every selected check must pass for the rule to be considered satisfied; if any check fails, the rule&apos;s effect fires.</li>
                    <li><strong>What it does at runtime:</strong> The engine looks up each check ID in its internal switch statement and runs the corresponding evaluation against the contact, the tenant, and the message context. See Section 6.5 for the full catalog with status badges.</li>
                    <li><strong>What to type:</strong> Pick the checks that map to the legal/operational requirement the rule encodes. Most rules need 1&ndash;3 checks. Some checks accept parameters (eg. <code>minConsent</code>, <code>cap</code>); set those in the same form.</li>
                    <li><strong>If you get it wrong:</strong> Adding a check that is currently <em>Pending engine support</em> (Section 6.5) makes the rule a no-op even though it appears active. Conversely, omitting a check that the statute requires lets non-compliant sends through.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Remediation</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The operator-facing &ldquo;how do I fix this?&rdquo; text shown in the Decision Log when this rule blocks or conditions a send.</li>
                    <li><strong>What it does at runtime:</strong> Display only; surfaces in the Decision Log entry and in the tenant&apos;s pre-send preview.</li>
                    <li><strong>What to type:</strong> An imperative sentence that names the concrete fix. <em>&ldquo;Capture written express consent before retrying.&rdquo;</em> Not <em>&ldquo;Consent missing.&rdquo;</em></li>
                    <li><strong>If you get it wrong:</strong> Operators reading a wall of blocked decisions cannot tell what to do; investigations take longer. No runtime impact.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Source Reference</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The legal or regulatory citation that justifies this rule&apos;s existence.</li>
                    <li><strong>What it does at runtime:</strong> Stored on the rule and shown in the Decision Log. Also used as the fallback for the Tenant-facing Citation if that field is left blank.</li>
                    <li><strong>What to type:</strong> Statute name, section number, regulator docket, or internal compliance memo. Eg. <em>&ldquo;TCPA 47 U.S.C. §227(b)(1)(A)&rdquo;</em> or <em>&ldquo;FCC 23-107, ¶31&rdquo;</em>.</li>
                    <li><strong>If you get it wrong:</strong> In a regulatory inquiry, an auditor cannot trace why a rule blocked a send. Operationally fine but legally weak.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Tenant-facing Summary (optional)</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> A single plain-English sentence that replaces the per-check bullets shown on the tenant&apos;s Policy page.</li>
                    <li><strong>What it does at runtime:</strong> When set, the tenant Policy page renders this sentence instead of the auto-generated check bullets. Display only &mdash; never affects the gate.</li>
                    <li><strong>What to type:</strong> Optional. Use when the per-check rendering would be confusing to a non-compliance reader. Eg. <em>&ldquo;Recipient must have written express opt-in before any wireless marketing SMS.&rdquo;</em></li>
                    <li><strong>If you get it wrong:</strong> Tenants see a wording you didn&apos;t intend. No runtime impact &mdash; the engine never reads this field.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Tenant-facing Citation (optional)</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> The short citation shown under the rule&apos;s entry on the tenant Policy page.</li>
                    <li><strong>What it does at runtime:</strong> Display only. Falls back to <strong>Source Reference</strong> when blank.</li>
                    <li><strong>What to type:</strong> Short, recognisable citation. Eg. <em>&ldquo;TCPA §227(b)(1)(A)&rdquo;</em> or <em>&ldquo;CAN-SPAM §7704&rdquo;</em>.</li>
                    <li><strong>If you get it wrong:</strong> Tenants see a less useful citation. No runtime impact.</li>
                  </ul>
                </div>

                <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white">Overridable by Tenants</h4>
                  <ul className="mt-2 space-y-1 text-sm list-disc pl-5">
                    <li><strong>What it is:</strong> A toggle that lets tenants adjust this rule&apos;s <em>parameters</em> (not its existence) from their own settings.</li>
                    <li><strong>What it does at runtime:</strong> When ON, the engine reads tenant-side overrides for params like <code>cap</code> or <code>minConsent</code>. When OFF, the system value is used exclusively. Hidden for Layer 0 rules.</li>
                    <li><strong>What to type:</strong> ON for tenant-tunable knobs (eg. raise their own frequency cap, tighten their own consent floor). OFF for safety-critical rules a tenant must not loosen.</li>
                    <li><strong>If you get it wrong:</strong> Turning ON a safety-critical rule could let an aggressive tenant disable a gate that protects them legally. Default to OFF unless you have a clear reason.</li>
                  </ul>
                </div>
              </div>

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.3 The three layers &mdash; which one do I pick?
              </h3>
              <p>
                The Layer choice is the most consequential field in the form because it changes
                <em> when</em> the rule fires and <em>which</em> other rules can short-circuit it.
                Quick decision aid:
              </p>
              <ul className="space-y-2 list-disc pl-5">
                <li>
                  Authoring a rule that <strong>grants initial INFORMATIONAL consent at contact
                  creation</strong> (eg. &ldquo;in the US auto-grant informational SMS
                  consent&rdquo;)? &rarr; <strong>Layer 0 (Consent Capture)</strong>. Cannot use
                  Required Checks. Effect can only be ALLOW or BLOCK.
                </li>
                <li>
                  Authoring a rule that <strong>blocks a send because the contact never opted
                  in</strong>, is on a DNC list, or fails a national-registry scrub? &rarr;{' '}
                  <strong>Layer 1 (Eligibility)</strong>. If this layer blocks, Layer 2 is
                  skipped entirely.
                </li>
                <li>
                  Authoring a rule about <strong>calling hours, frequency caps, or content
                  compliance</strong> (eg. CAN-SPAM unsubscribe link)? &rarr; <strong>Layer 2
                  (Constraint)</strong>. Runs only after Layer 1 has passed for all rules.
                </li>
              </ul>
              <MermaidDiagram
                chart={DIAGRAM_LAYER_DECISION}
                caption="In-engine layer evaluation order. The first BLOCK in any layer short-circuits the rest of the pipeline; CONDITIONs accumulate."
              />

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.4 The check catalog
              </h3>
              <p>
                These are the 21 checks the engine knows how to evaluate. The status badge tells
                you whether the check is fully implemented today, depends on tenant data being
                populated, or is reserved for a future engine release (currently a no-op).
              </p>
              <DocCallout variant="warning" title="Read the status badge before adding a check to a BLOCK rule">
                A check marked <strong>Pending engine support</strong> returns <code>true</code>{' '}
                unconditionally. If it is the only check on a BLOCK rule, the rule appears active
                in the UI but the engine will never block. Pair it with at least one Live or
                Tenant-config check, or wait for the engine release that activates it.
              </DocCallout>

              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-white">
                    Live <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200">9 checks</span>
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Fully evaluated by the engine today. Safe to use in BLOCK rules.
                  </p>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {CHECK_CATALOG.live.map((c) => (
                      <CheckCard key={c.id} {...c} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-white">
                    Tenant config required <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">6 checks</span>
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Fully evaluated by the engine, but only meaningful when the underlying tenant
                    or contact data is populated. The check fails closed if the data is missing.
                  </p>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {CHECK_CATALOG.tenantConfig.map((c) => (
                      <CheckCard key={c.id} {...c} />
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-white">
                    Pending engine support <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200">6 checks</span>
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    Reserved for content/runtime concerns that the engine does not yet evaluate.
                    Currently returns <code>true</code> &mdash; do not rely on for BLOCK rules in
                    production.
                  </p>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {CHECK_CATALOG.pending.map((c) => (
                      <CheckCard key={c.id} {...c} />
                    ))}
                  </div>
                </div>
              </div>

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.5 Worked examples
              </h3>
              <p>
                Three end-to-end authoring walkthroughs. Each one shows the exact values to type in
                every field, then the decision the engine produces for a representative send.
              </p>

              <h4 className="mt-6 font-semibold text-slate-900 dark:text-white">
                Example A &mdash; Block marketing SMS to US wireless without express consent (TCPA)
              </h4>
              <DocStepList
                steps={[
                  { title: 'Jurisdiction', description: 'US-FED' },
                  { title: 'Layer', description: 'Eligibility (Layer 1)' },
                  { title: 'Effect', description: 'BLOCK' },
                  { title: 'Channel', description: 'SMS' },
                  { title: 'Purpose', description: 'MARKETING' },
                  { title: 'B2B/B2C', description: 'B2C' },
                  { title: 'Required Checks', description: 'CHK_WIRELESS_MARKETING_CONSENT_OK with params { ifWirelessMinConsent: "EXPRESS_OPTIN" }' },
                  { title: 'Priority', description: '10 (safety-critical, runs first)' },
                  { title: 'Source Reference', description: 'TCPA 47 U.S.C. §227(b)(1)(A)' },
                  { title: 'Tenant-facing summary', description: '"We require express opt-in before sending marketing texts to mobile numbers."' },
                  { title: 'Tenant-facing citation', description: 'TCPA §227(b)(1)(A)' },
                  { title: 'Expected decision', description: 'Marketing SMS to a MOBILE contact without EXPRESS_OPTIN consent → trace shows the check FAILing, decision = BLOCK. Landline/VOIP contacts fall back to the standard channel consent gate and may still be allowed.' },
                ]}
              />

              <h4 className="mt-6 font-semibold text-slate-900 dark:text-white">
                Example B &mdash; Add a CASL condition to Canadian marketing email
              </h4>
              <DocStepList
                steps={[
                  { title: 'Jurisdiction', description: 'CA' },
                  { title: 'Layer', description: 'Eligibility (Layer 1)' },
                  { title: 'Effect', description: 'CONDITION' },
                  { title: 'Channel', description: 'EMAIL' },
                  { title: 'Purpose', description: 'MARKETING' },
                  { title: 'B2B/B2C', description: 'Blank (both)' },
                  { title: 'Required Checks', description: 'CHK_CASL_CONSENT_VALID with params { impliedPurchaseWindowMonths: 24, impliedInquiryWindowMonths: 6 }' },
                  { title: 'Priority', description: '50' },
                  { title: 'Source Reference', description: 'CASL S.C. 2010, c. 23' },
                  { title: 'Tenant-facing summary', description: '"Canadian recipients require valid CASL express or implied consent."' },
                  { title: 'Expected decision', description: 'Marketing email to a CA contact whose CASL consent is valid → decision = ALLOW with a CONDITION annotation in the trace for downstream logging. Without valid consent → falls through to whatever other rules cover that recipient.' },
                ]}
              />

              <h4 className="mt-6 font-semibold text-slate-900 dark:text-white">
                Example C &mdash; Cap promotional SMS at 3 per 24h (Layer 2 constraint)
              </h4>
              <DocStepList
                steps={[
                  { title: 'Jurisdiction', description: 'GLOBAL' },
                  { title: 'Layer', description: 'Constraint (Layer 2)' },
                  { title: 'Effect', description: 'BLOCK' },
                  { title: 'Channel', description: 'SMS' },
                  { title: 'Purpose', description: 'MARKETING' },
                  { title: 'Required Checks', description: 'CHK_FREQUENCY_CAP_24H_OK with params { cap: 3 }' },
                  { title: 'Priority', description: '200 (runs after eligibility-layer rules)' },
                  { title: 'Overridable by Tenants', description: 'ON — tenants may raise their own cap' },
                  { title: 'Tenant-facing summary', description: '"No more than three marketing texts to the same recipient in any 24-hour window."' },
                  { title: 'Expected decision', description: 'Fourth marketing SMS to the same recipient within 24h → trace shows the cap check FAILing → decision = BLOCK. The previous three sends were allowed and counted toward the rolling window.' },
                ]}
              />

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.6 Edit, deactivate, delete
              </h3>
              <p>
                Three operations, three different impacts on the audit trail:
              </p>
              <ul className="space-y-3 list-disc pl-5">
                <li>
                  <strong>Edit</strong> &mdash; every Save creates a new versioned snapshot. The
                  previous version is retained for the audit log. Trace logs always reference the
                  version that was live at the time of the send, so you can always answer
                  &ldquo;what was the rule wording when this decision was made?&rdquo;
                </li>
                <li>
                  <strong>Deactivate</strong> &mdash; flips the rule&apos;s <code>isActive</code>{' '}
                  flag to false. The delivery worker stops loading the rule on the next send. The
                  rule still exists in the database and can be re-activated. Historical Decision
                  Log entries that referenced this rule are unaffected.
                </li>
                <li>
                  <strong>Delete</strong> &mdash; permanently removes the rule row from the
                  database. Versioned snapshots and audit log entries are kept, but the rule
                  itself is gone. Reserve for rules that were authored in error and never should
                  have existed.
                </li>
              </ul>
              <DocCallout variant="warning" title="Prefer Deactivate over Delete">
                Decision Log entries reference rule IDs. Deleting a rule that has produced real
                decisions in production can make historical traces look unexplained. Deactivate
                first; delete only if you are certain you never need to re-enable the rule and you
                accept the gap in the audit story.
              </DocCallout>

              <h3 className="mt-8 text-lg font-semibold text-slate-900 dark:text-white">
                6.7 Common mistakes
              </h3>
              <ol className="space-y-3 list-decimal pl-5">
                <li>
                  <strong>Using a Pending-engine-support check on a BLOCK rule.</strong> The rule
                  will appear active in the UI but the engine will never block, because the check
                  always returns true. Pair it with a Live check, or wait for the engine release
                  that implements it.
                </li>
                <li>
                  <strong>Treating ALLOW as &ldquo;grants permission.&rdquo;</strong> Outside of
                  Layer 0, ALLOW is a fall-through, not an override. It does <em>not</em> bypass
                  other BLOCK rules in the same layer. To genuinely grant a permission, edit the
                  Layer 0 Consent Capture rule or capture an explicit consent record.
                </li>
                <li>
                  <strong>Scoping a rule B2C when your contact data has nulls.</strong> The engine
                  treats <code>Contact.b2bOrB2c = null</code> as a non-match against a rule
                  scoped <code>B2C</code>. Those contacts will skip the rule. If your contact data
                  is uncertain, leave B2B/B2C blank.
                </li>
                <li>
                  <strong>Authoring a safety-critical BLOCK at priority 100.</strong> Lower-priority
                  rules that BLOCK first will short-circuit the layer. Put safety-critical rules
                  at priority 0&ndash;50 so they evaluate before everything else.
                </li>
                <li>
                  <strong>Editing the Tenant-facing summary expecting it to change the
                  gate.</strong> The engine never reads that field. It is for the tenant Policy
                  page only. If you need to change the gate, edit the Required Checks or the
                  Effect.
                </li>
                <li>
                  <strong>Leaving Channel or Purpose blank on a channel-specific statute.</strong>{' '}
                  A TCPA wireless rule with Channel blank will fire on email too, which is wrong.
                  Always pin the rule to the channel and purpose the underlying statute actually
                  covers.
                </li>
              </ol>
              <DocPageLink to="/settings/policy/jurisdictions" label="Open Jurisdiction Rules" />
            </DocSection>

            {/* =================================================== */}
            {/* 7. Checks                                           */}
            {/* =================================================== */}
            <DocSection
              id="checks"
              eyebrow="Section 7"
              title="The Checks Library"
            >
              <p>
                A <strong>check</strong> is a single yes-or-no question PRE can ask about a send. Rules
                are made of one or more checks. The Check Library is the catalogue of all questions the
                engine knows how to ask — when you author a rule, you pick checks from this catalogue.
              </p>
              <p>The catalogue groups checks into five categories:</p>
              <ul className="space-y-3 list-disc pl-5">
                <li>
                  <strong>Consent checks</strong> — &ldquo;Has this recipient agreed to be contacted on
                  this channel, for this purpose, at the required level?&rdquo; Consent levels range
                  from implicit (an existing business relationship) up to written express opt-in (a
                  signed disclosure required for US marketing calls).
                </li>
                <li>
                  <strong>Do-Not-Contact checks</strong> — &ldquo;Is this number on our internal block
                  list, or on a national registry?&rdquo; National registry checks also enforce a
                  freshness rule: scrub data older than the configured window (typically 31 days) is
                  treated as missing.
                </li>
                <li>
                  <strong>Calling hours checks</strong> — &ldquo;Is it currently within an allowed
                  sending window in the recipient&apos;s local time?&rdquo;
                </li>
                <li>
                  <strong>Frequency cap checks</strong> — &ldquo;Have we contacted this recipient too
                  many times in the last day, week, or month?&rdquo;
                </li>
                <li>
                  <strong>Content compliance checks</strong> — &ldquo;Does the message include the
                  required identifiers — unsubscribe link, postal address, accurate sender — for this
                  jurisdiction?&rdquo;
                </li>
              </ul>
              <p>
                You normally do not need to edit the catalogue. It exists so the same check definition
                can be reused across many rules and many jurisdictions — change a check&apos;s
                user-facing wording in one place and it updates everywhere the check is used.
              </p>
              <DocPageLink to="/settings/policy/checks" label="Open the Check Library" />
            </DocSection>

            {/* =================================================== */}
            {/* 7. Calling Hours                                    */}
            {/* =================================================== */}
            <DocSection
              id="calling-hours"
              eyebrow="Section 8"
              title="Calling Hours & Business Windows"
            >
              <p>
                A <strong>calling hour window</strong> is a permitted sending window — a range of
                hours, on certain days of the week, in a specific timezone. PRE checks the current time
                at the recipient against the windows that apply to the send.
              </p>
              <MermaidDiagram
                chart={DIAGRAM_CALLING_HOURS}
                caption="A typical 8 AM – 9 PM window. Sends inside the window proceed; sends outside are blocked or deferred."
              />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Anatomy of a window
              </h3>
              <ul className="space-y-2 list-disc pl-5">
                <li>
                  <strong>Jurisdiction</strong> — which place this window applies to (eg. US-FL).
                </li>
                <li>
                  <strong>Channel and purpose</strong> — optional. A window can target voice marketing
                  specifically, or apply to everything.
                </li>
                <li>
                  <strong>Start and end time</strong> — hours and minutes, eg. 08:00 to 21:00.
                </li>
                <li>
                  <strong>Days of week</strong> — eg. Monday through Friday only, or every day.
                </li>
                <li>
                  <strong>Timezone</strong> — almost always &ldquo;recipient local&rdquo;, which means
                  PRE evaluates the window in the recipient&apos;s timezone, not yours.
                </li>
              </ul>
              <DocCallout variant="warning" title="Calling hours are evaluated in the recipient's local time">
                If you are sitting in New York at 8 PM and your recipient is in Los Angeles, it is 5 PM
                for them, and a 9-AM-to-9-PM California window will allow the send. The recipient&apos;s
                clock is what matters, not yours.
              </DocCallout>
              <DocCallout variant="danger" title="Unknown timezone = no send">
                If a contact has no timezone information and PRE cannot infer one from their phone or
                state, calling-hours checks fail closed — the send is blocked. This is on purpose. The
                fix is to enrich the contact record (set their state or timezone) so PRE can evaluate
                the window correctly.
              </DocCallout>
              <DocPageLink to="/settings/policy/hours" label="Open Calling Hours" />
            </DocSection>

            {/* =================================================== */}
            {/* 8. DNC Management                                   */}
            {/* =================================================== */}
            <DocSection
              id="dnc"
              eyebrow="Section 9"
              title="DNC Management"
            >
              <p>
                Do-Not-Contact (DNC) management comes in two flavours: your own internal list, and
                external national registries.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Internal DNC
              </h3>
              <p>
                The internal DNC list is platform-wide. Any number on it is hard-blocked from outbound
                SMS, regardless of consent or jurisdiction. The match can be exact (a single phone
                number), an area-code rule (block the whole 415 area), or a prefix rule (block
                everything starting with +1-800).
              </p>
              <p>
                Internal DNC is the right tool for: complaints, regulatory takedowns, repeated abuse
                reports, and emergency suppressions. PRE still runs the full evaluation when an
                internal-DNC number is matched, so you get a complete audit log — only the actual
                provider call is skipped.
              </p>
              <DocPageLink to="/settings/policy/dnc/internal" label="Open Internal DNC" />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                National Registries
              </h3>
              <p>
                National registries are external lists maintained by regulators — the US National DNC
                Registry, Brazil&apos;s Não Me Perturbe, Mexico&apos;s REUS and REPEP, and so on. PRE
                consults a contact&apos;s status against each applicable registry before allowing
                marketing.
              </p>
              <DocCallout variant="warning" title="The 31-day freshness rule">
                National DNC scrub data has a shelf life. The US registry, for example, requires that
                marketing call lists be scrubbed within 31 days. PRE enforces this: if the last scrub
                timestamp for a contact is older than the configured window, PRE treats the data as
                missing and the check fails closed. Schedule your scrubs accordingly.
              </DocCallout>
              <DocPageLink to="/settings/policy/dnc" label="Open DNC & Registries" />
            </DocSection>

            {/* =================================================== */}
            {/* 9. Frequency Caps                                   */}
            {/* =================================================== */}
            <DocSection
              id="frequency-caps"
              eyebrow="Section 10"
              title="Frequency Caps"
            >
              <p>
                A <strong>frequency cap</strong> is a ceiling on how often you may contact the same
                recipient. Common shapes: &ldquo;at most three marketing SMS to the same person in any
                24-hour window&rdquo;, or &ldquo;at most one cross-channel marketing contact per week
                on the same subject&rdquo;.
              </p>
              <p>
                Frequency caps are configured per jurisdiction × channel × purpose, with a maximum
                number of attempts and a rolling time window. They are evaluated by the constraint
                layer at send time — if the cap is exceeded, the send is either blocked or deferred,
                depending on the rule&apos;s effect.
              </p>
              <p>
                When a frequency cap blocks a send, the Decision Log entry will show how many prior
                attempts the engine counted, which makes it easy to confirm the right call was made.
              </p>
              <DocPageLink to="/settings/policy/dnc" label="Open Frequency Caps" />
            </DocSection>

            {/* =================================================== */}
            {/* 10. Contact Creation                                */}
            {/* =================================================== */}
            <DocSection
              id="contact-creation"
              eyebrow="Section 11"
              title="Contact Creation & Layer 0"
            >
              <p>
                The moment a contact is created — manually, via CSV import, via an integration —
                Layer 0 of PRE runs. Its job is to seed an initial set of consent records so that the
                contact starts life with a clean, defensible compliance state.
              </p>
              <MermaidDiagram
                chart={DIAGRAM_CONTACT_CREATION}
                caption="The Layer 0 sequence. Informational consent is auto-granted where allowed; marketing requires explicit opt-in."
              />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                What gets seeded
              </h3>
              <p>
                By default — that is, in the absence of a Layer 0 rule that says otherwise —{' '}
                <strong>informational</strong> consent is auto-granted on contact creation. This is the
                consent that lets you send delivery confirmations, appointment reminders, password
                resets, and similar service messages.
              </p>
              <p>
                <strong>Marketing</strong> consent is <em>never</em> auto-granted in jurisdictions
                where a Layer 0 rule blocks it — which is the case in every US state and most other
                supported jurisdictions. To send marketing, you need to record an explicit opt-in
                separately.
              </p>
              <DocCallout variant="warning" title="Marketing is never auto-granted in the US">
                If you import 10,000 contacts and immediately try to send them a marketing campaign,
                PRE will block every single send. This is the right behaviour. Capture marketing consent
                through an opt-in form or import a separately-verified consent file before sending.
              </DocCallout>
              <p>
                If you need to change Layer 0 behaviour — for example, to block informational consent
                auto-grant in a new jurisdiction that requires double opt-in — that is what the Contact
                Creation page is for.
              </p>
              <DocPageLink to="/settings/policy/consent-capture" label="Open Contact Creation Defaults" />
            </DocSection>

            {/* =================================================== */}
            {/* 11. AI Classification                               */}
            {/* =================================================== */}
            <DocSection
              id="ai-classification"
              eyebrow="Section 12"
              title="AI Message Classification"
            >
              <p>
                When a campaign is created, an AI model tags each message with a purpose — either
                Marketing or Informational. The purpose tag is what PRE uses to pick which rules apply,
                so the tag has real compliance consequences. A transactional reminder tagged
                &ldquo;marketing&rdquo; would be blocked for most contacts; a marketing offer tagged
                &ldquo;informational&rdquo; would skip the strict consent checks.
              </p>
              <p>
                The AI is usually right, but not always. When the model is uncertain, it flags the
                classification for human review and the message lands in a queue.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                What to do with the review queue
              </h3>
              <DocStepList
                steps={[
                  {
                    title: 'Read the message',
                    description:
                      'The queue card shows the message snippet, the AI&apos;s proposed tag, and its confidence score.',
                  },
                  {
                    title: 'Decide the correct purpose',
                    description:
                      'Operational and transactional messages (delivery updates, appointment reminders, account alerts) are Informational. Anything promoting a product, offer, or call-to-action is Marketing.',
                  },
                  {
                    title: 'Override if needed',
                    description:
                      'Click the override dropdown and pick the correct tag. The override is logged so an auditor can see who made the call.',
                  },
                ]}
              />
              <DocPageLink to="/settings/policy/ai" label="Open AI Classification" />
            </DocSection>

            {/* =================================================== */}
            {/* 12. Reading the Decision                            */}
            {/* =================================================== */}
            <DocSection
              id="the-decision"
              eyebrow="Section 13"
              title="Reading the Decision"
            >
              <p>
                Every PRE evaluation produces one of three decisions. Three different states, plus
                three different annotations you may see attached.
              </p>
              <MermaidDiagram
                chart={DIAGRAM_DECISION_STATES}
                caption="The three decision states, with the kinds of annotations each may carry."
              />
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                The three decisions
              </h3>
              <ul className="space-y-3 list-disc pl-5">
                <li>
                  <strong>Allow</strong> — Send immediately. All checks passed.
                </li>
                <li>
                  <strong>Allow with Conditions</strong> — Send is permitted but a condition must be
                  satisfied first. Typical example: &ldquo;wait until 9 AM in the recipient&apos;s
                  timezone&rdquo;. The delivery layer handles the deferral automatically.
                </li>
                <li>
                  <strong>Block</strong> — Do not send. The decision entry will name the failing
                  rule(s) and propose remediation.
                </li>
              </ul>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Conditions vs Risk Flags vs Remediation
              </h3>
              <p>These three are easy to confuse. They mean different things:</p>
              <ul className="space-y-3 list-disc pl-5">
                <li>
                  <strong>Conditions</strong> are requirements the send must satisfy before it is
                  actually delivered. They can apply to Allow-with-Conditions decisions. Examples:
                  &ldquo;defer until calling hours&rdquo;, &ldquo;include unsubscribe link&rdquo;.
                </li>
                <li>
                  <strong>Risk flags</strong> are warnings that something about the input was uncertain
                  — &ldquo;we guessed the state from the area code&rdquo;, &ldquo;we don&apos;t know if
                  this number is mobile or landline&rdquo;. Risk flags do not block; they alert. They
                  often appear on Allow decisions as data-quality signals.
                </li>
                <li>
                  <strong>Remediation</strong> is the proposed fix when a send is blocked.
                  &ldquo;Capture written express consent&rdquo;. &ldquo;Wait for the next scrub to
                  complete&rdquo;. Read the remediation when you are debugging why a campaign produced
                  a wall of Block decisions.
                </li>
              </ul>
            </DocSection>

            {/* =================================================== */}
            {/* 13. The Decision Log                                */}
            {/* =================================================== */}
            <DocSection
              id="decision-log"
              eyebrow="Section 14"
              title="The Decision Log"
            >
              <p>
                The Decision Log is the audit trail. Every PRE evaluation — whether the result was
                Allow, Allow-with-Conditions, or Block — is recorded with the full reasoning that led to
                it. This is your defence in a regulatory dispute and your debugging tool when something
                does not behave as expected.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                What a decision entry contains
              </h3>
              <ul className="space-y-2 list-disc pl-5">
                <li>
                  <strong>The inputs</strong> — recipient, channel, purpose, jurisdiction, local time at
                  recipient, internal DNC status, national registry status, frequency counts.
                </li>
                <li>
                  <strong>The eligibility flags</strong> — a compact view of what the contact had
                  consent for at the moment of evaluation.
                </li>
                <li>
                  <strong>The rule trace</strong> — every rule that fired, in order, with each
                  rule&apos;s pass/fail result and its citation.
                </li>
                <li>
                  <strong>Conditions, risk flags, and remediation</strong> — the three annotation types
                  described above.
                </li>
                <li>
                  <strong>Plain-English summary</strong> — a one-sentence summary of what the engine
                  decided and why, so non-technical readers do not have to interpret the raw trace.
                </li>
              </ul>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                How to use the filters
              </h3>
              <p>
                The most common investigations are: &ldquo;why is this campaign producing so many
                blocks?&rdquo;, &ldquo;what was the decision on this specific contact?&rdquo;, and
                &ldquo;has anything been blocked in the last 24 hours that I should know about?&rdquo;.
                The filter bar covers all three — filter by campaign, contact, decision type, and date
                range, then expand individual rows to read the trace.
              </p>
              <DocPageLink to="/settings/policy/decisions" label="Open the Decision Log" />
            </DocSection>

            {/* =================================================== */}
            {/* 14. Common Scenarios                                */}
            {/* =================================================== */}
            <DocSection
              id="scenarios"
              eyebrow="Section 15"
              title="Common Scenarios"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Scenario A — Marketing SMS to California at 9 AM vs 9 PM
              </h3>
              <p>
                A campaign is sent at 9 AM Pacific to a California consumer who has given written
                express opt-in. PRE resolves the jurisdiction to US-CA, finds the marketing-SMS rules
                for California, confirms consent at the required level, confirms the contact is not on
                any DNC list, and confirms the time is inside the permitted window. Decision: Allow.
              </p>
              <p>
                The same campaign sent at 9 PM Pacific to the same consumer: every check still passes,
                but the time-of-day check produces a condition. Decision: Allow with Conditions, defer
                to the next permitted window.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Scenario B — Email to a contact whose state is unknown
              </h3>
              <p>
                Email does not depend on calling hours or area-code jurisdiction the same way SMS does,
                but it does depend on the contact&apos;s state for state-specific marketing rules. If
                the state is missing, PRE falls back to the phone number; if the phone is also missing,
                PRE evaluates against the federal baseline only. A risk flag is attached so you know
                the evaluation was less precise than it could have been.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Scenario C — Reading a BLOCK log
              </h3>
              <p>
                Open the Decision Log, filter to the blocked decision, expand the row. Look at the rule
                trace — the first rule that fails with a Block effect is the cause. Read its citation
                (the statute) and its remediation note (the fix). Common causes: missing consent at the
                required level, DNC match, frequency cap exceeded, calling hours violated.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Scenario D — Brazilian marketing SMS to a B2B contact
              </h3>
              <p>
                PRE resolves the jurisdiction to Brazil. Brazil&apos;s rules apply. Because the contact
                is B2B, the rules tagged for B2C consumers do not fire — only the B2B-applicable
                rules and any &ldquo;any audience&rdquo; rules are evaluated. Decision varies based on
                consent and the Não Me Perturbe registry status.
              </p>
              <h3 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Scenario E — A contact opts out
              </h3>
              <p>
                A recipient replies STOP to an SMS, or clicks the unsubscribe link in an email. Their
                consent record for that channel and purpose is flipped to OPTOUT. The next time a send
                is requested, the consent check fails and PRE blocks the send. Other channels and
                purposes that the contact has consented to are unaffected — opting out of marketing SMS
                does not opt the contact out of informational SMS or marketing email.
              </p>
            </DocSection>

            {/* =================================================== */}
            {/* 15. Bundles & Backups                               */}
            {/* =================================================== */}
            <DocSection
              id="bundles-backups"
              eyebrow="Section 16"
              title="Bundles & Backups"
            >
              <p>
                Your entire rule corpus — jurisdictions, rules, calling hours, DNC registries,
                frequency caps, check definitions — can be exported as a single <strong>bundle</strong>.
                Bundles are useful for three things:
              </p>
              <ul className="space-y-2 list-disc pl-5">
                <li>
                  <strong>Backups</strong> — snapshot the corpus before a major rule change so you can
                  roll back if something breaks.
                </li>
                <li>
                  <strong>Promotion between environments</strong> — author rules in a staging
                  environment, then promote the bundle to production.
                </li>
                <li>
                  <strong>Comparison</strong> — diff two bundles to see exactly what changed between
                  two points in time.
                </li>
              </ul>
              <p>
                Imports are idempotent — re-importing the same bundle does not duplicate rules; it
                updates them in place by natural key.
              </p>
              <DocPageLink to="/settings/policy/bundle" label="Open Bundle Export / Import" />
            </DocSection>

            {/* =================================================== */}
            {/* 16. Glossary                                        */}
            {/* =================================================== */}
            <DocSection
              id="glossary"
              eyebrow="Section 17"
              title="Glossary"
            >
              <dl className="space-y-3">
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Auto-Grant</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    Layer 0 behaviour where a consent record is automatically created on contact
                    creation. Almost always limited to informational purposes.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">B2B / B2C</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    Whether the recipient is a business or a consumer. Many rules — especially
                    DNC-related ones — apply only to one or the other.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Bundle</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A single exported snapshot of the entire PRE rule corpus.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Channel</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    The transport — SMS, Email, or Voice. RCS and social-message channels normalize to
                    SMS for compliance purposes.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Check</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A single yes-or-no question PRE can ask about a send. Rules are made of checks.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Condition</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A requirement attached to an Allow-with-Conditions decision that must be satisfied
                    before delivery — for example, deferring to a calling-hours window.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">ConsentRecord</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    The row that captures &ldquo;recipient X has granted/declined consent for channel Y,
                    purpose Z, at level L, captured on date D, with proof P&rdquo;.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Effect</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    What a rule does when its checks fail: Block, Condition, or Allow.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Frequency Cap</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A ceiling on how many messages may be sent to the same recipient within a rolling
                    time window.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Internal DNC</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    The platform&apos;s own Do-Not-Contact list. Hard-blocks SMS regardless of consent
                    or jurisdiction.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Jurisdiction</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A place whose laws govern what you may send. Examples: GLOBAL, US-FED, US-CA, CA,
                    BR.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Layer</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    One of three groupings PRE uses to organise rules by when they fire: Consent
                    Capture (at contact creation), Eligibility (before send), Constraints (at send
                    time).
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">National Registry</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A regulator-maintained Do-Not-Contact list — eg. the US National DNC Registry. PRE
                    consults registries before allowing marketing sends.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Purpose</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    Why the message is being sent: Marketing or Informational. Different purposes are
                    governed by different rules.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Remediation</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    The proposed fix attached to a Block decision — what the operator or recipient
                    must do to make a future send eligible.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Risk Flag</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A warning that some input to the evaluation was uncertain — for example, the state
                    was guessed from the area code. Risk flags do not block; they alert.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Rule</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    A single compliance requirement — a combination of layer, channel, purpose, B2B/B2C,
                    required checks, and effect.
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-slate-900 dark:text-white">Trace</dt>
                  <dd className="ml-4 text-slate-600 dark:text-slate-400">
                    The ordered list of rules that fired during an evaluation, with each rule&apos;s
                    pass/fail result. Visible in the Decision Log.
                  </dd>
                </div>
              </dl>
              <DocCallout variant="tip" title="Still have questions?">
                The other Policy pages — Jurisdictions, Calling Hours, DNC, AI Classification, the
                Decision Log, and the Check Library — each have inline help text on the page itself.
                Open the page you have a question about and look for the small grey notes near each
                section.
              </DocCallout>
            </DocSection>
          </main>
        </div>
      </div>
    </div>
  );
}
